/** Mode tabs — every one but "all" narrows the dashboard to a single
 *  service type, matched under every spelling a stored shipment may carry
 *  (`tl` covers `tl` and `ftl`; see `storedServiceTypeSpellings`).
 *  Order here is the order they render. */
export const FREIGHT_DASHBOARD_MODE_TABS = [
    "all",
    "ltl",
    "tl",
    "drayage",
    "parcel",
    "ocean",
    "air",
] as const

/** Neither of these is a mode: both reuse whatever mode/vendor/date filters
 *  are already set, which is why selecting them doesn't reset `mode`. */
export const CARRIERS_TAB = "carriers" as const
export const ACTIVITY_TAB = "activity" as const

export const FREIGHT_DASHBOARD_TABS = [
    ...FREIGHT_DASHBOARD_MODE_TABS,
    CARRIERS_TAB,
    ACTIVITY_TAB,
] as const

export type FreightDashboardMode = (typeof FREIGHT_DASHBOARD_MODE_TABS)[number]
export type FreightDashboardTab = (typeof FREIGHT_DASHBOARD_TABS)[number]

/**
 * Resolves which tabs an org may see.
 *
 * An unset or EMPTY selection falls back to `FREIGHT_DASHBOARD_TABS`
 * rather than to nothing: a dashboard with no tabs is unusable, and an org
 * that has never opened these settings must not silently lose its dashboard.
 * Unknown ids are dropped so a stale stored selection — a tab since renamed
 * or removed — degrades to the remaining valid ones instead of rendering a
 * dead tab.
 */
export const resolveVisibleFreightDashboardTabs = (
    configured: readonly string[] | null | undefined
): readonly FreightDashboardTab[] => {
    if (!configured || configured.length === 0) {
        return FREIGHT_DASHBOARD_TABS
    }
    const selected = new Set(configured)
    const visible = FREIGHT_DASHBOARD_TABS.filter(tab => selected.has(tab))
    return visible.length > 0 ? visible : FREIGHT_DASHBOARD_TABS
}
